import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * The suite's one identity, resolved from the Ortam hub.
 *
 * Every application in the suite shares a single account: the hub's session
 * cookie (`ortam_session`) is host-keyed and ignores ports, so signing in on
 * this screen signs the user in everywhere. The auth calls travel over this
 * application's own Vite proxy (`/api/auth` → hub backend :4040), so no
 * cross-origin dance is ever needed and the cookie lands on the host the
 * application was opened under.
 */

export interface HubUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  status: string;
  role: string;
}

interface HubAuthState {
  user: HubUser | null;
  /** True while the boot-time session probe is in flight — render a skeleton, not the gate. */
  booting: boolean;
  /** True when the hub backend did not answer — a different thing from being signed out. */
  unreachable: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  retry: () => void;
}

const HubAuthContext = createContext<HubAuthState | null>(null);

/** Same-origin path the Vite proxy forwards to the hub backend. */
const AUTH_BASE = "/api/auth";

async function hubPost(path: string, body: unknown) {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string; user?: HubUser };
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export function HubAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<HubUser | null>(null);
  const [booting, setBooting] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Boot-time probe: an honest `{ user: null }` is a state to render (the gate),
  // while a network failure is reported as "unreachable" — never as signed out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBooting(true);
      setUnreachable(false);
      try {
        const res = await fetch(`${AUTH_BASE}/status`, { credentials: "include" });
        const data = (await res.json().catch(() => ({}))) as { user?: HubUser | null };
        if (cancelled) return;
        setUser(data.user ?? null);
      } catch {
        if (!cancelled) {
          setUser(null);
          setUnreachable(true);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await hubPost("/login", { email, password });
    setUser(data.user ?? null);
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const data = await hubPost("/register", { name, email, password });
    setUser(data.user ?? null);
  }, []);

  // The cookie is shared by every port, so signing out clears it at the hub —
  // dropping only local state would leave every sibling application signed in.
  const logout = useCallback(() => {
    void hubPost("/logout", {}).catch(() => undefined);
    setUser(null);
  }, []);

  // The suite's shared top bar (chrome.js) fires `chrome:logout` from its
  // account menu; every application that mounts it signs out through this hook.
  useEffect(() => {
    const onChromeLogout = () => logout();
    window.addEventListener("chrome:logout", onChromeLogout);
    return () => window.removeEventListener("chrome:logout", onChromeLogout);
  }, [logout]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const value = useMemo(
    () => ({ user, booting, unreachable, login, register, logout, retry }),
    [user, booting, unreachable, login, register, logout, retry]
  );
  return <HubAuthContext.Provider value={value}>{children}</HubAuthContext.Provider>;
}

export function useHubAuth(): HubAuthState {
  const ctx = useContext(HubAuthContext);
  if (!ctx) throw new Error("useHubAuth must be used within HubAuthProvider");
  return ctx;
}
